package org.teddapp.project.ui.backup

import androidx.compose.ui.graphics.SolidColor

import org.teddapp.project.ui.LocalTeddFonts

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.sync.SyncStatus
import org.teddapp.project.sync.SyncUiState
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.settings.LocalSupabaseConnector
import org.teddapp.project.ui.settings.LocalSyncBindings

/**
 * Cartes de l'écran Sync en mise en page compacte : en-tête, état, exports, connexion, historique.
 */

@Composable
internal fun PhoneBackupHeader(title: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .statusBarsPadding()
            .padding(start = 18.dp, end = 18.dp, top = 14.dp, bottom = 8.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            TeddLogo(size = 22.dp)
            Text("SAUVEGARDE", color = TeddTokens.InkMuted, style = org.teddapp.project.ui.TeddType.Overline)
        }
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = title,
            style = org.teddapp.project.ui.TeddType.Title1,
            color = TeddTokens.Ink,
        )
    }
}

/**
 * Hint affiché juste au-dessus du SupabaseConnectForm quand le user n'est pas
 * connecté. Précise que la sync cloud est **optionnelle** — l'app marche en local.
 */
@Composable
internal fun CloudOptionalHint() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 8.dp, bottom = 2.dp),
    ) {
        Text(
            "SYNCHRONISATION (FACULTATIVE)",
            color = TeddTokens.InkMuted,
            fontSize = 10.5.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.6.sp,
            modifier = Modifier.padding(start = 4.dp, bottom = 4.dp),
        )
        Text(
            "teddApp fonctionne sans serveur : tout est sur cet appareil, et tu peux exporter ta base " +
                "à tout moment. Connecte-toi à un serveur seulement pour retrouver tes données sur tes autres appareils.",
            color = TeddTokens.InkMuted,
            fontSize = 11.5.sp,
            modifier = Modifier.padding(start = 4.dp, end = 4.dp, bottom = 8.dp),
        )
    }
}

@Composable
internal fun GapAlertCard(gap: BackupGap) {
    val isDanger = gap.level == BackupGapLevel.Danger
    val color = when (gap.level) {
        BackupGapLevel.Danger -> TeddTokens.Danger
        BackupGapLevel.Warning -> TeddTokens.Warning
        BackupGapLevel.Info -> TeddTokens.LimeStrong
        BackupGapLevel.Ok -> TeddTokens.Success
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(
                Brush.linearGradient(listOf(color.copy(alpha = 0.15f), color.copy(alpha = 0.04f))),
                RoundedCornerShape(TeddTokens.RMd),
            )
            .border(1.dp, color.copy(alpha = 0.3f), RoundedCornerShape(TeddTokens.RMd))
            .padding(14.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier.size(32.dp).clip(RoundedCornerShape(TeddTokens.RSm))
                .background(color, RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "bell", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                if (isDanger) "Alerte sync" else "Avertissement",
                color = color,
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
            )
            Text(gap.message, color = TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        }
    }
}

/**
 * Hero card design — gradient vert→cyan riche, texte blanc, 3 stats inline (Ce mois / Volume / Cloud).
 * Inclut un bouton "Sync maintenant" pleine largeur en dessous.
 */
@Composable
internal fun StatusHeroCard(
    lastOk: BackupEntry?,
    monthCount: Int,
    totalKb: Int,
    totalRows: Int,
    cloudConnected: Boolean,
    isLocalOnly: Boolean,
    pendingChanges: Int,
    syncing: Boolean,
    onSyncNow: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RLg))
            // Bloc d'encre, comme les autres blocs de tête sur téléphone.
            .background(TeddTokens.Ink, RoundedCornerShape(TeddTokens.RLg))
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.OnAction.copy(alpha = 0.2f), RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.OnAction.copy(alpha = 0.3f), RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "check", size = 20.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    if (isLocalOnly) "Stockage local" else "Dernière synchronisation",
                    color = TeddTokens.OnAction.copy(alpha = 0.85f),
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    text = when {
                        isLocalOnly -> "Tes données sont sur cet appareil"
                        lastOk != null -> "${fmtDayLabel(lastOk.dayIso)} · ${lastOk.hhmm}"
                        else -> "Jamais"
                    },
                    color = TeddTokens.OnAction,
                    fontSize = 17.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = (-0.3).sp,
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            HeroInlineStat(label = "Volume", value = "$totalKb Ko")
            HeroInlineStat(label = "Lignes", value = totalRows.toString())
            if (!isLocalOnly) {
                HeroInlineStat(label = "Ce mois", value = "$monthCount sur 2")
            }
            Spacer(modifier = Modifier.weight(1f))
            if (isLocalOnly) {
                HeroLocalBadge()
            } else {
                HeroCloudStat(connected = cloudConnected)
            }
        }
        // Bouton sync intégré — texte adapté au mode local
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(42.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.OnAction.copy(alpha = 0.22f), RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.OnAction.copy(alpha = 0.35f), RoundedCornerShape(TeddTokens.RSm))
                .clickable(
                    enabled = !syncing && !isLocalOnly,
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onSyncNow,
                ),
            contentAlignment = Alignment.Center,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                TeddIcon(
                    name = when {
                        isLocalOnly -> "lock"
                        syncing -> "clock"
                        else -> "repeat"
                    },
                    size = 14.dp,
                    color = TeddTokens.OnAction.copy(alpha = if (isLocalOnly) 0.7f else 1f),
                    strokeWidth = 2.4.dp,
                )
                Text(
                    text = when {
                        isLocalOnly -> "Aucun serveur relié"
                        syncing -> "Synchronisation…"
                        pendingChanges > 0 -> "Synchroniser ($pendingChanges en attente)"
                        else -> "Synchroniser"
                    },
                    color = TeddTokens.OnAction.copy(alpha = if (isLocalOnly) 0.85f else 1f),
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
    }
}

@Composable
internal fun HeroLocalBadge() {
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RFull))
            .background(TeddTokens.OnAction.copy(alpha = 0.18f), RoundedCornerShape(TeddTokens.RFull))
            .border(1.dp, TeddTokens.OnAction.copy(alpha = 0.25f), RoundedCornerShape(TeddTokens.RFull))
            .padding(horizontal = 10.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Box(modifier = Modifier.size(6.dp).clip(CircleShape).background(TeddTokens.OnAction, CircleShape))
        Text(
            "Mode local",
            color = TeddTokens.OnAction,
            fontSize = 11.5.sp,
            fontWeight = FontWeight.SemiBold,
        )
    }
}

@Composable
internal fun HeroInlineStat(label: String, value: String) {
    Column {
        Text(label, color = TeddTokens.OnAction.copy(alpha = 0.85f), fontSize = 11.sp)
        Text(
            value,
            color = TeddTokens.OnAction,
            fontSize = 15.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = LocalTeddFonts.current.mono,
        )
    }
}

@Composable
internal fun HeroCloudStat(connected: Boolean) {
    Column(horizontalAlignment = Alignment.End) {
        Text("Serveur", color = TeddTokens.OnAction.copy(alpha = 0.85f), fontSize = 11.sp)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            Box(
                modifier = Modifier
                    .size(6.dp)
                    .clip(CircleShape)
                    .background(if (connected) TeddTokens.Success else TeddTokens.OnAction.copy(alpha = 0.4f), CircleShape),
            )
            Text(
                if (connected) "Relié" else "Hors ligne",
                color = TeddTokens.OnAction,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

@Composable
internal fun ExportGrid(
    onJsonExport: () -> Unit,
    onSqlExport: () -> Unit,
    jsonAvailable: Boolean,
    sqlAvailable: Boolean,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .weight(1f)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(
                    if (jsonAvailable) TeddTokens.SignatureGradient else Brush.linearGradient(listOf(TeddTokens.SurfaceSunken, TeddTokens.SurfaceSunken)),
                    RoundedCornerShape(TeddTokens.RMd),
                )
                .clickable(
                    enabled = jsonAvailable,
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onJsonExport,
                )
                .padding(vertical = 14.dp, horizontal = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(5.dp),
        ) {
            TeddIcon(
                name = "arrow",
                size = 18.dp,
                color = if (jsonAvailable) TeddTokens.OnAction else TeddTokens.InkMuted,
                strokeWidth = 2.4.dp,
            )
            Text(
                "Exporter en JSON",
                color = if (jsonAvailable) TeddTokens.OnAction else TeddTokens.InkMuted,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        Column(
            modifier = Modifier
                .weight(1f)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .clickable(
                    enabled = sqlAvailable,
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onSqlExport,
                )
                .padding(vertical = 14.dp, horizontal = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(5.dp),
        ) {
            TeddIcon(
                name = "settings",
                size = 18.dp,
                color = if (sqlAvailable) TeddTokens.Ink else TeddTokens.InkMuted,
                strokeWidth = 2.dp,
            )
            Text(
                "Exporter en SQL",
                color = if (sqlAvailable) TeddTokens.Ink else TeddTokens.InkMuted,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

@Composable
internal fun AutoSyncToggleCard(on: Boolean, onChange: (Boolean) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(32.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "spark", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text("Sauvegarde automatique", color = TeddTokens.Ink, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
            Text(
                "La fréquence se règle sur ordinateur.",
                color = TeddTokens.InkMuted,
                fontSize = 10.5.sp,
            )
        }
        ToggleSwitch(on = on, onChange = { onChange(!on) }, small = true)
    }
}

@Composable
internal fun ConnectionCompactCard(connector: org.teddapp.project.ui.settings.SupabaseConnector) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(32.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Success, RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text("Projet Supabase", color = TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            Text(
                connector.currentUrl,
                color = TeddTokens.InkMuted,
                fontSize = 10.5.sp,
                fontFamily = LocalTeddFonts.current.mono,
                maxLines = 1,
            )
        }
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = { connector.disconnect() },
                )
                .padding(horizontal = 10.dp, vertical = 6.dp),
        ) {
            Text(
                "Déco.",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

@Composable
internal fun HistorySection(history: List<BackupEntry>, cloudAvailable: Boolean) {
    Column {
        Text(
            "HISTORIQUE RÉCENT",
            color = TeddTokens.InkMuted,
            fontSize = 10.5.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.6.sp,
            modifier = Modifier.padding(start = 4.dp, top = 4.dp, bottom = 6.dp),
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd)),
        ) {
            if (history.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().padding(20.dp), contentAlignment = Alignment.Center) {
                    Text(
                        text = if (cloudAvailable) "Aucune sync encore — touche Sync maintenant"
                               else "Rien à afficher : aucune synchronisation n'a eu lieu.",
                        color = TeddTokens.InkMuted,
                        fontSize = 12.sp,
                    )
                }
            } else {
                history.take(6).forEachIndexed { i, entry ->
                    if (i > 0) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(1.dp)
                                .padding(start = 48.dp)
                                .background(TeddTokens.Line),
                        )
                    }
                    PhoneHistoryRow(entry = entry)
                }
            }
        }
    }
}

@Composable
internal fun PhoneHistoryRow(entry: BackupEntry) {
    val color = when (entry.status) {
        BackupStatus.Success -> TeddTokens.Success
        BackupStatus.Partial -> TeddTokens.Warning
        BackupStatus.Failed -> TeddTokens.Danger
    }
    val iconName = if (entry.status == BackupStatus.Success) "check" else "bell"
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(26.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(color.copy(alpha = 0.10f), RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = iconName, size = 12.dp, color = color, strokeWidth = 2.4.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                "${entry.trigger.label} · ${entry.format.label}",
                color = TeddTokens.Ink,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
            )
            Text(
                "${fmtDayLabel(entry.dayIso)} · ${entry.hhmm} · ${entry.sizeKb} Kb",
                color = TeddTokens.InkMuted,
                fontSize = 10.5.sp,
                fontFamily = LocalTeddFonts.current.mono,
            )
        }
    }
}

